// The signer's part of a request (SIGN-FE-01): "Recevoir un code" says where the code was sent and
// when it expires, then a field for the six digits and "Signer". A new code can be asked for a minute
// after the previous one, with a visible countdown and a polite announcement that is spoken twice
// only (when the wait starts and when it ends), not every second. The code lives in this component's
// state: it is never put in the address, in storage, in a log or in a mutation cache.
import type { SignatureCodeSentDtoOutput } from '@batiflow/api-client';
import { FormField, SectionCard } from '@batiflow/ui';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Mail20Regular, Pen20Regular } from '@fluentui/react-icons';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { signWithCode, useApplyRequest, useSendSignatureCode } from './api';
import { useWhen } from './bits';
import { DeclineDialog } from './decline-dialog';
import {
  cleanCode,
  errorMessage,
  isCode,
  ltr,
  needsNewCode,
  resendRemaining,
  type SignatureRequest,
} from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"text":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358","D305fb":"f14tep7y"},"code":{"Bahqtrf":"f1fedwem","ublp8o":"f1jd1jey","Be2twd7":"fod5ikn","kgocjf":["f1r2v9sp","f1va4sj6"]},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"countdown":{"sj55zd":"fkfq4zb","B7salou":"f1pbl6dw"},"hidden":{"qhf8xq":"f1euv43f","a9b677":"frkrog8","Bqenvij":"f1mpe4l3","B68tc82":"f1p9o1ba","Bmxbyg5":"f1sil6mw","Bh84pgu":"f1w1vubc","Huce71":"fz5stix"}});

/** The current time, refreshed every second only while `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [active]);
  return now;
}

export function SignPanel({ orgId, request }: { orgId: string; request: SignatureRequest }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const notify = useNotify();
  const { moment } = useWhen();
  const apply = useApplyRequest(orgId);
  const sendCode = useSendSignatureCode();
  const field = useRef<HTMLInputElement>(null);

  const [sent, setSent] = useState<SignatureCodeSentDtoOutput | null>(null);
  const [localSentAt, setLocalSentAt] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);

  // A code asked for earlier (before a reload) still counts: the API remembers when it was sent.
  const sentAt =
    localSentAt ?? (request.me.codeSentAt ? new Date(request.me.codeSentAt).getTime() : null);
  const hasCode = sentAt !== null;
  const now = useNow(hasCode);
  const remaining = sentAt === null ? 0 : resendRemaining(sentAt, now);

  const ask = async () => {
    setError(null);
    try {
      const result = await sendCode.mutateAsync({ orgId, requestId: request.id });
      setSent(result);
      setLocalSentAt(Date.now());
      setCode('');
      field.current?.focus();
    } catch (failure) {
      setError(errorMessage(t, failure));
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!isCode(code)) {
      setError(t('sign.errors.codeFormat'));
      field.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const fresh = await signWithCode(orgId, request.id, code);
      setCode('');
      await apply(fresh);
      notify({ intent: 'success', message: t('sign.signed') });
    } catch (failure) {
      setError(errorMessage(t, failure));
      setCode('');
      // A locked or expired code needs a new one; a wrong one can be typed again.
      if (!needsNewCode(failure)) field.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={t('sign.title')} headingLevel={2}>
      <div className={styles.body}>
        <p className={styles.text}>{t('sign.intro')}</p>

        {!hasCode ? (
          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={sendCode.isPending ? <Spinner size="tiny" /> : <Mail20Regular />}
              disabledFocusable={sendCode.isPending}
              onClick={() => {
                void ask();
              }}
            >
              {sendCode.isPending ? t('sign.sending') : t('sign.receive')}
            </Button>
          </div>
        ) : (
          <>
            <p className={styles.text}>
              {sent
                ? t('sign.sentTo', {
                    address: ltr(sent.sentTo),
                    channel: t(`sign.channels.${sent.channel}`),
                    time: moment(sent.expiresAt),
                  })
                : t('sign.sentEarlier', { time: moment(new Date(sentAt).toISOString()) })}
            </p>
            <form
              className={styles.form}
              noValidate
              onSubmit={(event) => {
                void submit(event);
              }}
            >
              <FormField
                label={t('sign.codeLabel')}
                hint={t('sign.codeHint')}
                error={error}
                required
              >
                <Input
                  input={{ ref: field }}
                  className={styles.code}
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={12}
                  onChange={(_event, data) => {
                    setCode(cleanCode(data.value));
                    setError(null);
                  }}
                />
              </FormField>
              <div className={styles.actions}>
                <Button
                  type="submit"
                  appearance="primary"
                  icon={busy ? <Spinner size="tiny" /> : <Pen20Regular />}
                  disabledFocusable={busy}
                >
                  {busy ? t('sign.signing') : t('sign.submit')}
                </Button>
                <Button
                  appearance="secondary"
                  disabledFocusable={remaining > 0 || sendCode.isPending}
                  onClick={() => {
                    void ask();
                  }}
                >
                  {t('sign.resend')}
                </Button>
                {remaining > 0 ? (
                  <span className={styles.countdown} aria-hidden="true">
                    {t('sign.resendIn', { seconds: remaining })}
                  </span>
                ) : null}
              </div>
              <span role="status" className={styles.hidden}>
                {remaining > 0 ? t('sign.resendWait') : t('sign.resendReady')}
              </span>
            </form>
          </>
        )}

        {!hasCode && error ? (
          <MessageBar intent="error" layout="multiline" role="alert">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        <div className={styles.actions}>
          <Button
            appearance="subtle"
            onClick={() => {
              setDeclining(true);
            }}
          >
            {t('sign.decline')}
          </Button>
        </div>
      </div>
      {declining ? (
        <DeclineDialog
          orgId={orgId}
          request={request}
          onClose={() => {
            setDeclining(false);
          }}
        />
      ) : null}
    </SectionCard>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/sign-panel.griffel.css";